import React, { useState, useEffect, useRef } from "react";
import { map, filter, size, some, omit } from "lodash";

import Attachments from "./Attachments";
import Modal from "components/shared/Modal";
import { useCreateComment, useUpdateComment } from "./api";

import styles from "./ReplyComposer.module.css";
import { Controller, FormProvider, useForm } from "react-hook-form";
import FormActions from "components/shared/form/fields/FormActions";
import { faSpinner, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Editor from "components/shared/markdownEditor/MarkdownEditor";

const filterRemoved = (items) =>
  filter(items, (item) => item.state != "removed");

interface ReplyComposerTypes {
  type?: "replies" | "answers";
  activityId: string;
  reply: {
    id: string;
    content: string;
    files: Array<unknown>;
    images: Array<unknown>;
  };
  isOpen?: boolean;
  selectedTab: string;
  initialQuoteData?: {
    author: {
      displayName: string;
      url: string;
      is_external: boolean;
    };
    content: string;
    id: string;
  } | null;

  saveCallback(...args: unknown[]): unknown;

  cancelCallback(...args: unknown[]): unknown;
}

type FormData = ReplyComposerTypes["reply"];

export default function ReplyComposer({
  type = "replies",
  activityId,
  reply,
  isOpen = false,
  selectedTab,
  initialQuoteData,
  saveCallback,
  cancelCallback,
}: ReplyComposerTypes) {
  const editorRef = useRef<{ focus: () => void }>();
  const [open, setOpen] = useState(isOpen);
  const [fullscreen, setFullscreen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [quoteData, setQuoteData] = useState(initialQuoteData);

  const methods = useForm<FormData>({
    defaultValues: { content: reply.content },
  });
  const { handleSubmit, watch, control } = methods;

  // Update local quoteData when the prop changes
  useEffect(() => {
    setQuoteData(initialQuoteData);
  }, [initialQuoteData]);

  const openComposer = () => {
    setOpen(true);
    setTimeout(() => {
      editorRef.current?.focus();
    });
  };
  const closeComposer = (reset?) => {
    if (reset) {
      reset();
    }
    setQuoteData(null);
    setOpen(false);
  };

  // const openFullscreen = () => {
  //   setFullscreen(true);
  // };
  const closeFullscreen = () => {
    setFullscreen(false);
  };

  const onSuccess = (result) => {
    if (saveCallback) {
      saveCallback(result);
    }

    setIsProcessing(false);
    closeComposer();
  };

  const onCancel = () => {
    if (cancelCallback) {
      cancelCallback();
    }
    closeComposer();
  };

  const onError = () => {
    setIsProcessing(false);
  };

  const { mutate: createComment } = useCreateComment({
    onSuccess,
    onError,
  });
  const { mutate: updateComment } = useUpdateComment({ onSuccess, onError });

  function submit(data: FormData) {
    setIsProcessing(true);
    const body = {
      ...data,
      files: map(filterRemoved(data.files), (file) => file.id),
      images: map(filterRemoved(data.images), (image) => image.id),
    };

    if (reply.id) {
      return updateComment({
        activityId: activityId,
        replyId: reply.id,
        type,
        ...body,
      });
    } else {
      return createComment({
        activityId: activityId,
        type,
        ...body,
        quotedReplyId: quoteData?.id,
      });
    }
  }

  // To react to external isOpen prop changes
  useEffect(() => {
    isOpen ? openComposer() : closeComposer();
  }, [isOpen]);

  const files = watch("files");
  const images = watch("images");
  const fileProcessing = some(files, { state: "uploading" });
  const imageProcessing = some(images, { state: "uploading" });

  const handleCancelQuote = (e) => {
    e.preventDefault();
    setQuoteData(null);
  };

  return open ? (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)}>
        {quoteData && (
          <div className="quote_block pl-2 border-l-4 border-primary bg-gray-50 py-1 rounded-md mb-4 relative">
            <div className="flex justify-between items-start">
              <a
                href={quoteData.author.url}
                className="font-semibold member-link member-name mb-1"
              >
                {`${quoteData.author.displayName}${
                  quoteData.author.is_external
                    ? ` (${I18n.t("js.external_member.badge")})`
                    : ""
                }`}
              </a>
              <div className="cancel-quote btn btn-sm text-muted absolute top-2 right-2">
                <button
                  className="btn btn-sm p-0"
                  onClick={handleCancelQuote}
                  title={I18n.t("js.cancel")}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>
            <div
              className="text-gray-600 mt-1"
              dangerouslySetInnerHTML={{ __html: quoteData.content }}
            />
          </div>
        )}
        {fullscreen ? (
          <Modal
            static
            fullscreen
            onClose={closeFullscreen}
            title={I18n.t(`js.activitystreams.${type}.author_hint`)}
          >
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <Editor
                  {...omit(field, ["ref"])}
                  editorRef={editorRef}
                  enableTixxtMentions
                />
              )}
            />
          </Modal>
        ) : (
          <div className="flex gap-2">
            {!reply.id && Preload.current_member && (
              <div className="member-image shrink-0 self-start">
                <img
                  className="w-10 h-10"
                  src={Preload.current_member.images["40x40@2"]}
                  alt={Preload.current_member.displayName}
                />
              </div>
            )}
            <div className="flex flex-col">
              <div className="min-h-48 grow">
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <Editor
                      {...omit(field, ["ref"])}
                      editorRef={editorRef}
                      enableTixxtMentions
                    />
                  )}
                />
              </div>
              <Attachments
                selectedTab={selectedTab}
                filesCount={size(filterRemoved(files))}
                imagesCount={size(filterRemoved(images))}
              />
            </div>
          </div>
        )}
        <FormActions
          className={"mt-2"}
          onCancel={onCancel}
          saveText={I18n.t(`js.activitystreams.${type}.submit`)}
          saveIconProps={
            isProcessing || fileProcessing || imageProcessing
              ? {
                  icon: faSpinner,
                  spin: true,
                }
              : undefined
          }
          saveDisabled={isProcessing || fileProcessing || imageProcessing}
        />
      </form>
    </FormProvider>
  ) : (
    <div className="flex gap-2">
      {!reply.id && Preload.current_member && (
        <div className="member-image shrink-0 self-start">
          <img
            className="w-10 h-10"
            src={Preload.current_member.images["40x40@2"]}
            alt={Preload.current_member.displayName}
          />
        </div>
      )}
      <button
        key="dummy"
        className={`ReplyComposer object-content-input form-control text-gray-400 text-left ${styles.closed}`}
        onClick={openComposer}
      >
        <span className="placeholder">
          {I18n.t(`js.activitystreams.${type}.author_hint`)}
        </span>
      </button>
    </div>
  );
}
